// frontend/src/api.js - COMPLETE FIXED VERSION
import axios from "axios";

// ============================================
// API CONFIGURATION
// ============================================

const getApiUrl = () => {
  // First check for environment variable
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Fallback based on NODE_ENV
  if (process.env.NODE_ENV === "production") {
    return "https://kenali-enterprise-api.onrender.com/api";
  }

  return "http://localhost:5002/api";
};

const API = axios.create({
  baseURL: getApiUrl(),
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  withCredentials: true,
});

// ============================================
// REQUEST INTERCEPTOR
// ============================================

API.interceptors.request.use(
  (config) => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      if (user?.token) {
        config.headers.Authorization = `Bearer ${user.token}`;
      }
    } catch (e) {
      console.error("Error parsing user from localStorage:", e);
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ============================================
// RESPONSE INTERCEPTOR (Token Refresh)
// ============================================

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle network errors with retry
    if (!error.response && !error.request) {
      console.warn("⚠️ Network error, will retry...");
      // Retry up to 3 times for network errors
      if (!originalRequest._retryCount) {
        originalRequest._retryCount = 0;
      }
      if (originalRequest._retryCount < 3) {
        originalRequest._retryCount += 1;
        await new Promise((resolve) =>
          setTimeout(resolve, 1000 * originalRequest._retryCount),
        );
        return API(originalRequest);
      }
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (originalRequest.url?.includes("/auth/")) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return API(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshResponse = await API.post("/auth/refresh-token");
        const { token } = refreshResponse.data;

        if (token) {
          const user = JSON.parse(localStorage.getItem("user") || "{}");
          if (user) {
            user.token = token;
            localStorage.setItem("user", JSON.stringify(user));
          }
          processQueue(null, token);
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return API(originalRequest);
        } else {
          throw new Error("No token in refresh response");
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem("user");
        localStorage.removeItem("cart");
        // Use React Router navigation if possible, otherwise fallback to window.location
        if (!window.location.pathname.includes("/login")) {
          window.location.href = "/login";
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // ============================================
    // FIXED: 403 Handler - Don't redirect for all 403s
    // ============================================
    if (error.response?.status === 403) {
      // Check if this is an admin-only endpoint
      const isAdminEndpoint =
        originalRequest.url?.includes("/admin/") ||
        originalRequest.url?.includes("/dashboard/") ||
        originalRequest.url?.includes("/users/admin/") ||
        originalRequest.url?.includes("/orders/admin/") ||
        originalRequest.url?.includes("/shipments/admin/") ||
        originalRequest.url?.includes("/preorders/admin/");

      // Get user from localStorage to check role
      let user = null;
      try {
        user = JSON.parse(localStorage.getItem("user") || "{}");
      } catch (e) {
        console.error("Error parsing user:", e);
      }

      // If it's an admin endpoint and user is not admin, redirect to unauthorized
      // Otherwise, let the error propagate - team members might just not have permission for that specific action
      if (
        isAdminEndpoint &&
        user &&
        user.role !== "admin" &&
        user.role !== "super_admin"
      ) {
        // Only redirect if the user is trying to access admin-only features
        if (!window.location.pathname.includes("/unauthorized")) {
          window.location.href = "/unauthorized";
        }
      } else {
        // For team members, just reject the promise so the component can handle it
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);

// ============================================
// AUTH ENDPOINTS
// ============================================

export const login = (email, password) =>
  API.post("/auth/login", { email, password });

export const register = (userData) => API.post("/auth/register", userData);

export const getMe = () => API.get("/auth/me");

export const updateProfile = (data) => API.put("/auth/profile", data);

export const changePassword = (oldPassword, newPassword) =>
  API.put("/auth/change-password", { oldPassword, newPassword });

export const refreshToken = () => API.post("/auth/refresh-token");

export const logout = () => API.post("/auth/logout");

export const forgotPassword = (email) =>
  API.post("/auth/forgot-password", { email });

export const resetPassword = (token, password) =>
  API.post(`/auth/reset-password/${token}`, { password });

export const verifyEmail = (token) => API.get(`/auth/verify/${token}`);

// ============================================
// USER ENDPOINTS
// ============================================

export const updateUserPreferences = (data) =>
  API.put("/users/preferences", data);

export const getMyPreOrders = () => API.get("/users/my-preorders");

export const getUserOrders = () => API.get("/orders/my-orders");

export const getMyUserStats = () => API.get("/users/my-stats");

// ============================================
// PUBLIC USER ENDPOINTS (No Auth Required)
// ============================================

export const getPublicTeamMembers = () => API.get("/users/public/team");

export const getPublicProfile = (userId) =>
  API.get(`/users/public/profile/${userId}`);

export const getPublicProfileByUsername = (username) =>
  API.get(`/users/public/profile/username/${username}`);

// ============================================
// ADMIN USER MANAGEMENT
// ============================================

export const getUsers = (params) => API.get("/users/admin/all", { params });

export const getUserById = (id) => API.get(`/users/admin/${id}`);

export const updateUser = (id, data) => API.put(`/users/admin/${id}`, data);

export const deleteUser = (id) => API.delete(`/users/admin/${id}`);

export const getAdminUserStats = () => API.get("/users/admin/stats");

export const createTeamMember = (userData) =>
  API.post("/users/admin/create", userData);

export const toggleUserStatus = (userId) =>
  API.patch(`/users/admin/${userId}/toggle-status`);

// ============================================
// STAFF ACCESS ENDPOINTS
// ============================================

export const getStaffUsers = (params) =>
  API.get("/users/staff/all", { params });

export const getStaffUserById = (id) => API.get(`/users/staff/${id}`);

// ============================================
// PRE-ORDER ENDPOINTS
// ============================================

export const createPreOrder = (data) => API.post("/preorders", data);

export const lookupPreOrders = (phone) =>
  API.get("/preorders/lookup", { params: { phone } });

export const getPreOrderById = (id) => API.get(`/preorders/${id}`);

export const cancelPreOrder = (id) => API.put(`/preorders/${id}/cancel`);

export const getAllPreOrders = (params) =>
  API.get("/preorders/admin/all", { params });

export const getPreOrderStats = () => API.get("/preorders/admin/stats");

export const updatePreOrderStatus = (id, data) =>
  API.put(`/preorders/${id}/status`, data);

// ============================================
// PRODUCT ENDPOINTS
// ============================================

export const getProducts = (params) => API.get("/products", { params });

export const getProductBySlug = (slug) => API.get(`/products/slug/${slug}`);

export const getProductById = (id) => API.get(`/products/${id}`);

export const createProduct = (data) => {
  if (data instanceof FormData) {
    return API.post("/products", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }
  return API.post("/products", data);
};

export const updateProduct = (id, data) => {
  if (data instanceof FormData) {
    return API.put(`/products/${id}`, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }
  return API.put(`/products/${id}`, data);
};

export const deleteProduct = (id) => API.delete(`/products/${id}`);

export const getFeaturedProducts = () => API.get("/products/featured/list");

export const getProductsByCategory = (category) =>
  API.get(`/products/category/${category}`);

export const getProductPriceTiers = (productId) =>
  API.get(`/products/${productId}/price-tiers`);

export const getBulkOptions = (productId) =>
  API.get(`/products/${productId}/bulk-options`);

export const getAvailableSizes = () => API.get("/products/sizes");

export const getPreOrderSizes = () => API.get("/products/preorder-sizes");

// ============================================
// PRODUCT REACTIONS & COMMENTS
// ============================================

export const addReaction = (productId, reactionType) =>
  API.post("/reactions", { productId, reactionType });

export const getUserReaction = (productId) =>
  API.get(`/reactions/user/${productId}`);

export const getReactionCounts = (productId) =>
  API.get(`/reactions/counts/${productId}`);

export const getComments = (productId, params) =>
  API.get(`/comments/product/${productId}`, { params });

export const addComment = (productId, comment, parentCommentId = null) =>
  API.post("/comments", { productId, comment, parentCommentId });

export const markCommentHelpful = (commentId) =>
  API.post(`/comments/${commentId}/helpful`);

export const deleteComment = (commentId) =>
  API.delete(`/comments/${commentId}`);

// ============================================
// CART ENDPOINTS
// ============================================

export const getCart = async () => {
  try {
    const response = await API.get("/cart");
    return response;
  } catch (error) {
    // If cart returns 400, try to create a new cart
    if (error.response?.status === 400 || error.response?.status === 404) {
      console.warn("Cart not found or invalid, retrying...");
      // Try one more time - server will create if needed
      return API.get("/cart");
    }
    throw error;
  }
};

export const addToCart = async (productId, quantity, fallbackData = null) => {
  const data = { productId, quantity };
  if (fallbackData) {
    data.name = fallbackData.name;
    data.price = fallbackData.price;
    data.unit = fallbackData.unit;
  }
  try {
    return await API.post("/cart/add", data);
  } catch (error) {
    // If cart doesn't exist, try to get it first then retry
    if (error.response?.status === 400) {
      try {
        // Try to get/create cart first
        await API.get("/cart");
        // Retry adding
        return await API.post("/cart/add", data);
      } catch (retryError) {
        throw retryError;
      }
    }
    throw error;
  }
};

export const updateCartItem = (itemId, quantity) =>
  API.put("/cart/update", { itemId, quantity });

export const removeFromCart = (itemId) => API.delete(`/cart/item/${itemId}`);

export const clearCart = () => API.delete("/cart/clear");

// ============================================
// ORDER ENDPOINTS
// ============================================

export const createOrder = (orderData) => API.post("/orders", orderData);

export const getOrderById = (id) => API.get(`/orders/${id}`);

export const cancelOrder = (id) => API.put(`/orders/${id}/cancel`);

export const getAllOrders = (params) =>
  API.get("/orders/admin/all", { params });

export const updateOrderStatus = (id, status) =>
  API.put(`/orders/${id}/status`, { status });

export const createBulkOrder = (orderData) =>
  API.post("/orders", { ...orderData, orderType: "bulk_farmer" });

export const getBulkOrdersSummary = () => API.get("/orders/admin/bulk-summary");

// ============================================
// SHIPMENT ENDPOINTS (Complete)
// ============================================

export const trackShipment = (trackingNumber) =>
  API.get(`/shipments/track/${trackingNumber}`);

export const getShipmentByOrder = (orderId) =>
  API.get(`/shipments/order/${orderId}`);

export const createShipment = (data) => API.post("/shipments", data);

export const getAllShipments = (params) =>
  API.get("/shipments/admin/all", { params });

export const updateShipmentStatus = (id, status, location, description) =>
  API.put(`/shipments/${id}/status`, { status, location, description });

// TEAM MEMBER ROUTES
export const getTeamShipments = (params) =>
  API.get("/shipments/team/all", { params });

export const getTeamActiveShipments = () =>
  API.get("/shipments/team/active");

export const getTeamShipmentStats = () =>
  API.get("/shipments/team/stats");

// INVENTORY SHIPMENT ROUTES
export const getInventoryShipments = (params) =>
  API.get("/shipments/inventory", { params });

export const createInventoryShipment = (data) =>
  API.post("/shipments/inventory", data);

export const updateShipmentStock = (id, data) =>
  API.put(`/shipments/${id}/stock`, data);

export const getShipmentStats = () =>
  API.get("/shipments/admin/stats");

// ============================================
// SUPPLIER ENDPOINTS
// ============================================

export const registerSupplier = (data) => API.post("/suppliers/register", data);

export const getSuppliers = (params) => API.get("/suppliers", { params });

export const getSupplierById = (id) => API.get(`/suppliers/${id}`);

export const updateSupplier = (id, data) => API.put(`/suppliers/${id}`, data);

export const approveSupplier = (id) => API.put(`/suppliers/${id}/approve`);

export const deleteSupplier = (id) => API.delete(`/suppliers/${id}`);

// ============================================
// INVENTORY ENDPOINTS
// ============================================

export const createInventoryFromShipment = (data) =>
  API.post("/inventory/from-shipment", data);

export const getInventory = (params) => API.get("/inventory", { params });

export const getInventoryById = (id) => API.get(`/inventory/${id}`);

export const updateInventory = (id, data) => API.put(`/inventory/${id}`, data);

export const deleteInventory = (id) => API.delete(`/inventory/${id}`);

export const getLowStockInventory = () => API.get("/inventory/low-stock");

export const getInventoryStats = () => API.get("/inventory/stats");

// ============================================
// CONTACT ENDPOINTS
// ============================================

export const createContactMessage = (data) => API.post("/contact", data);

export const getContactMessages = (params) => API.get("/contact", { params });

export const getContactMessageById = (id) => API.get(`/contact/${id}`);

export const respondToContactMessage = (id, data) =>
  API.put(`/contact/${id}/respond`, data);

export const updateMessageStatus = (id, data) =>
  API.put(`/contact/${id}/status`, data);

// ============================================
// CHAT ENDPOINTS
// ============================================

export const createChatSession = () => API.post("/chat/session");

export const sendChatMessage = (sessionId, message) =>
  API.post(`/chat/${sessionId}/message`, { message });

export const getChatHistory = (sessionId) =>
  API.get(`/chat/${sessionId}/history`);

export const rateChat = (sessionId, rating) =>
  API.post(`/chat/${sessionId}/rate`, { rating });

export const endChatSession = (sessionId) => API.put(`/chat/${sessionId}/end`);

// ============================================
// TESTIMONIAL ENDPOINTS
// ============================================

export const getApprovedTestimonials = (params) =>
  API.get("/testimonials/approved", { params });

export const createTestimonial = (data) => API.post("/testimonials", data);

export const getUserTestimonials = () =>
  API.get("/testimonials/my-testimonials");

export const updateTestimonial = (id, data) =>
  API.put(`/testimonials/${id}`, data);

export const deleteTestimonial = (id) => API.delete(`/testimonials/${id}`);

export const markTestimonialHelpful = (id) =>
  API.post(`/testimonials/${id}/helpful`);

export const getAllTestimonials = (params) =>
  API.get("/testimonials/admin/all", { params });

export const getTestimonialStats = () => API.get("/testimonials/admin/stats");

export const approveTestimonial = (id) =>
  API.put(`/testimonials/admin/${id}/approve`);

export const rejectTestimonial = (id, data) =>
  API.put(`/testimonials/admin/${id}/reject`, data);

export const featureTestimonial = (id) =>
  API.put(`/testimonials/admin/${id}/feature`);

export const adminRespondToTestimonial = (id, response) =>
  API.post(`/testimonials/admin/${id}/respond`, { response });

// ============================================
// PAYMENT ENDPOINTS
// ============================================

export const getPaymentMethods = () => API.get("/payments/methods");

export const addPaymentMethod = (data) => API.post("/payments/methods", data);

export const updatePaymentMethod = (id, data) =>
  API.put(`/payments/methods/${id}`, data);

export const removePaymentMethod = (id) =>
  API.delete(`/payments/methods/${id}`);

export const setDefaultPaymentMethod = (id) =>
  API.put(`/payments/methods/${id}/default`);

export const getPayoutHistory = () => API.get("/payments/payouts");

export const requestPayout = (data) => API.post("/payments/payouts", data);

export const getPayoutStats = () => API.get("/payments/payouts/stats");

// ============================================
// DASHBOARD ENDPOINTS
// ============================================

export const getDashboardStats = () => API.get("/dashboard/stats");

export const getRecentOrders = (limit = 10) =>
  API.get(`/dashboard/recent-orders?limit=${limit}`);

export const getLowStockProducts = (threshold = 10) =>
  API.get(`/dashboard/low-stock?threshold=${threshold}`);

export const getAnalytics = (period = "30d") =>
  API.get(`/dashboard/analytics?period=${period}`);

// ============================================
// TEAM MEMBER MANAGEMENT (Dashboard)
// ============================================

export const getTeamMemberStats = () => API.get("/dashboard/team/stats");

export const getPendingTeamMembers = (params) =>
  API.get("/dashboard/team/pending", { params });

export const getTeamMemberApprovalStats = () =>
  API.get("/dashboard/team/approval-stats");

export const approveTeamMember = (userId, notes) =>
  API.put(`/dashboard/team/approve/${userId}`, { notes });

export const rejectTeamMember = (userId, reason) =>
  API.put(`/dashboard/team/reject/${userId}`, { reason });

// ============================================
// EXPORT DEFAULT
// ============================================

export default API;

export { API };
